import { HubConnectionState } from "@microsoft/signalr";
import { IUnifiedAction } from "contexts/appsettings/interface";
import { SignalRContextProps } from "contexts/signalr/interface";
import { TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { Dispatch } from "react";
import { createPatch } from "rfc6902";
import { ADD_RECEIVED_MESSAGE, TlfContextProps } from "./interface";

/**
 * Notifies collaborators of a saved or patched TLF and records the sent message.
 *
 * @param lastSave - Persisted baseline used to calculate the collaborator update.
 * @param newTlfData - TLF returned by the successful save or patch request.
 * @param signalR - Collaboration connection and messaging commands, when available.
 * @param dispatch - Application dispatcher used to deduplicate the sent notification.
 */
export const sendSignalRPatch = async (
  lastSave: TlfContextProps["savedTlfData"],
  newTlfData: TlfItemReadDto,
  signalR: SignalRContextProps | null,
  dispatch: Dispatch<IUnifiedAction>,
): Promise<void> => {
  if (lastSave && signalR?.connection?.state === HubConnectionState.Connected) {
    const allowedKeys = Object.keys(lastSave).filter(
      (key) => !["saved", "changed", "inError", "history"].includes(key),
    );
    const sentMessage = await signalR.sendTlfUpdateToGroup(
      {
        updatedItemId: lastSave.id,
        patch: createPatch(
          Object.fromEntries(
            allowedKeys.map((key) => [key, lastSave[key as keyof typeof lastSave]]),
          ),
          Object.fromEntries(
            allowedKeys.map((key) => [key, newTlfData[key as keyof TlfItemReadDto]]),
          ),
        ),
      },
      `tlf${lastSave.id}`,
    );
    dispatch({
      operation: ADD_RECEIVED_MESSAGE,
      calledFrom: "sendSignalRPatch",
      receivedMessage: sentMessage,
    });
  }
};
